import {
  Column,
  Loader,
  Page,
  Row,
  Spinner,
} from '@oliasoft-open-source/react-ui-library';
import { DeleteModal } from 'client/components/delete.modal';
import translations from 'client/internationalization/translation-map.json';
import { RootState, TDispatch } from 'client/store/configureStore';
import {
  createAnalytics,
  createDashboard,
  dashboardSelected,
  deleteAnalytics,
  deleteDashboard,
  getAnalytics,
  getAnalyticsDashboards,
  updateAnalytic,
  updateDashboard,
} from 'client/store/entities/analytics/analytics';
import { getLoadingState, getSelectedDashboardId } from 'client/store/entities/analytics/selectors';
import {
  getQueries,
} from 'client/store/entities/query/query';
import { IAnalytic, IDashboard } from 'common/interfaces/analytics';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ConnectedProps, connect, useDispatch, useSelector,
} from 'react-redux';
import { DashboardDrawer } from './dashboard/dashboard-drawer';
import { AnalyticModal } from './cards/analytics-modal';

import { AnalyticsCards } from './cards/analytic-cards';
import { DashboardModal } from './dashboard/dashboard-modal';

const ModalTypes = Object.freeze({
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
});

const EntityTypes = Object.freeze({
  DASHBOARD: 'DASHBOARD',
  ANALYTIC: 'ANALYTIC',
});

type TEntityTypesKeys = keyof typeof EntityTypes;
type TEntityTypesValues = typeof EntityTypes[TEntityTypesKeys];

const containerStyle = { padding: '20px' };

const Analytics = ({
  queries,
}: PropsFromRedux) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<TDispatch>();
  const {
    isFetching,
  } = useSelector(getLoadingState);

  const [isModalOpen, setModalState] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string | null>(ModalTypes.CREATE);
  const [entityType, setEntityType] = useState<TEntityTypesValues | null>(null);
  const [itemForEdit, setEditedItem] = useState<IAnalytic | IDashboard | null>(null);

  const selectedDashboardId = useSelector(getSelectedDashboardId);

  const isCreateModalOpen = modalType === ModalTypes.CREATE;
  const isEditModalOpen = modalType === ModalTypes.UPDATE;
  const isDeleteModalOpen = modalType === ModalTypes.DELETE;

  const closeModal = () => {
    setEditedItem(null);
    setModalType(null);
    setModalState(false);
  };

  const handleCreateAnalytic = (analytic: IAnalytic) => {
    dispatch(createAnalytics({ ...analytic, dashboardid: selectedDashboardId }));
    closeModal();
  };

  const handleUpdateAnalytic = (analytic: IAnalytic) => {
    dispatch(updateAnalytic({ ...analytic, dashboardid: selectedDashboardId }));
    closeModal();
  };

  const handleDeleteAnalytic = () => {
    if (itemForEdit && (itemForEdit as IAnalytic)?.analyticid) {
      // @ts-ignore
      dispatch(deleteAnalytics(itemForEdit.analyticid, itemForEdit.dashboard));
    }
    closeModal();
  };

  const handleCreateDashboard = (dashboard: IDashboard) => {
    dispatch(createDashboard(dashboard));
    closeModal();
  };

  const handleUpdateDashboard = (dashboard: IDashboard) => {
    dispatch(updateDashboard(dashboard));
    closeModal();
  };

  const handleDeleteDashboard = () => {
    if (itemForEdit?.dashboardid) {
      dispatch(deleteDashboard(itemForEdit.dashboardid));
    }
    closeModal();
  };

  const handleOpenEditModal = (data: IAnalytic | IDashboard, entityType: TEntityTypesValues) => {
    setEntityType(entityType);
    setEditedItem(data);
    setModalType(ModalTypes.UPDATE);
    setModalState(true);
  };

  const handleOpenCreateModal = (entityType: TEntityTypesValues) => {
    setEntityType(entityType);
    setModalType(ModalTypes.CREATE);
    setModalState(true);
  };

  const handleOpenDeleteModal = (data: IAnalytic | IDashboard, entityType: TEntityTypesValues) => {
    setEntityType(entityType);
    setEditedItem(data);
    setModalType(ModalTypes.DELETE);
    setModalState(true);
  };

  const handleSelectDashboard = (dashboardId: string) => {
    dispatch(dashboardSelected(dashboardId));
  };

  useEffect(() => {
    dispatch(getAnalyticsDashboards());
    dispatch(getQueries());
    return () => {
      dispatch(dashboardSelected(''));
    };
  }, []);

  useEffect(() => {
    if (selectedDashboardId) {
      dispatch(getAnalytics(selectedDashboardId));
    }
  }, [selectedDashboardId]);

  const renderAnalyticCards = () => {
    if (!selectedDashboardId) {
      return null;
    }
    if (isFetching) {
      return <Loader text={`${t(translations.hierarchy_loading)}`} width="100%" height="500px" theme="light"><Spinner dark /></Loader>;
    }
    return (
      <AnalyticsCards
        onOpenDeleteModal={(data: IAnalytic) => handleOpenDeleteModal(data, EntityTypes.ANALYTIC)}
        onOpenEditModal={(data: IAnalytic) => handleOpenEditModal(data, EntityTypes.ANALYTIC)}
        onOpenCreateModal={() => handleOpenCreateModal(EntityTypes.ANALYTIC)}
      />
    );
  };

  return (
    <Page padding={false} scroll={false}>
      <Row spacing={0} height="100%">
        <DashboardDrawer
          selectedDashboardId={selectedDashboardId}
          onOpenCreateModal={() => handleOpenCreateModal(EntityTypes.DASHBOARD)}
          onEditDashboard={(data: IDashboard) => handleOpenEditModal(data, EntityTypes.DASHBOARD)}
          onDeleteDashboard={(data: IDashboard) => handleOpenDeleteModal(data, EntityTypes.DASHBOARD)}
          onSelectDashboard={handleSelectDashboard}
        />
        <Column scroll borderLeft>
          <div style={containerStyle}>
            {renderAnalyticCards()}
          </div>
        </Column>
      </Row>
      {(isCreateModalOpen || isEditModalOpen) && entityType === EntityTypes.ANALYTIC && (
      <AnalyticModal
        visible={isModalOpen}
        queries={queries}
        onClose={closeModal}
        isCreate={isCreateModalOpen}
        // @ts-ignore
        onSubmit={isEditModalOpen ? handleUpdateAnalytic : handleCreateAnalytic}
        itemForEdit={isEditModalOpen ? itemForEdit as IAnalytic : null}
      />
      )}
      {(isCreateModalOpen || isEditModalOpen) && entityType === EntityTypes.DASHBOARD && (
      <DashboardModal
        visible={isModalOpen}
        onClose={closeModal}
        onSubmit={isEditModalOpen ? handleUpdateDashboard : handleCreateDashboard}
        itemForEdit={isEditModalOpen ? itemForEdit as IDashboard : null}
      />
      )}
      {isDeleteModalOpen && itemForEdit && (
      <DeleteModal
        entity={itemForEdit}
        onSubmit={entityType === EntityTypes.ANALYTIC ? handleDeleteAnalytic : handleDeleteDashboard}
        onClose={closeModal}
      />
      )}
    </Page>
  );
};

// selectors
const mapStateToProps = ({ entities }: RootState) => ({
  queries: entities?.query.queries,
});

const mapDispatchToProps = {
  getQueries,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(Analytics);

export { Container as Analytics };
